/**
 * Local Pickup View Component
 *
 * Page-level orchestrator using a tab + bottom sheet architecture.
 * Three tabs: Browse (locations list + optional map overlay), Schedule, Cart (sheet).
 * Location details and cart slide up as bottom sheets rather than separate full-screen views.
 *
 * @element peach-local-pickup-view
 *
 * @remarks
 * Desktop keeps the two-column sidebar + right-panel layout.
 * Mobile uses the new tab bar at the bottom with bottom sheets.
 *
 * @example
 * ```html
 * <peach-local-pickup-view></peach-local-pickup-view>
 * <peach-local-pickup-view default-view="schedule"></peach-local-pickup-view>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { uiStore, type ActiveTab } from "@/stores/ui-store";
import { cartStore } from "@/features/cart/stores/cart-store";
import { locationStore } from "@/features/locations/stores/location-store";
import { mapViewStore } from "@/features/map/stores/map-view-store";
import { GeolocationService } from "@/services/geolocation-service";
import { urlState } from "@/services/url-state";
import { cn } from "@/utils/cn";
import "@/features/ui/icon";
import "@/features/ui/bottom-sheet";

// Import view components
import "@/features/map";
import "@/features/schedule";
import "@/features/map/components/locations-drawer";
import "@/features/map/components/location-details";
import "@/features/map/components/map-filters";
import "@/features/cart/components/cart-drawer";

// Logo asset
import logoUrl from "/logo-footer-red.svg?url";

/**
 * View type for URL state
 */
export type ViewType = "map" | "schedule";

/**
 * Storage key for persisting tab preference
 */
const TAB_STORAGE_KEY = "peach-local-pickup-tab";

@customElement("peach-local-pickup-view")
export class LocalPickupViewComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Default view on initial load ("map" maps to "browse", "schedule" maps to "schedule")
   */
  @property({ type: String })
  declare defaultView: ViewType;

  /**
   * Currently active tab
   */
  @state()
  declare private activeTab: ActiveTab;

  /**
   * Whether the map overlay is visible in the browse tab (mobile)
   */
  @state()
  declare private showMapOverlay: boolean;

  /**
   * Details sheet state - null when closed
   */
  @state()
  declare private detailsSheetLocationId: number | null;

  /**
   * Whether the cart sheet is open
   */
  @state()
  declare private cartOpen: boolean;

  /**
   * Cart item count for badge
   */
  @state()
  declare private cartItemCount: number;

  /**
   * Whether schedule has been activated (keeps it in DOM after first show)
   */
  @state()
  declare private scheduleActivated: boolean;

  /**
   * Whether geolocation request is in progress
   */
  @state()
  declare private isGettingLocation: boolean;

  /**
   * Store unsubscribe functions
   */
  private unsubscribeCart?: () => void;
  private unsubscribeUi?: () => void;
  private unsubscribeLocation?: () => void;

  /**
   * Flag to prevent duplicate skeleton removal
   */
  private skeletonsRemoved = false;

  constructor() {
    super();
    this.defaultView = "map";
    this.activeTab = this.getInitialTab();
    this.showMapOverlay = false;
    this.detailsSheetLocationId = null;
    this.cartOpen = false;
    this.cartItemCount = 0;
    this.scheduleActivated = this.activeTab === "schedule";
    this.isGettingLocation = false;
  }

  /**
   * @remarks
   * Data loading is handled by the page-map.ts entrypoint via
   * locationStore.getState().setLocationsUrl() -> fetchLocations().
   * This component subscribes to the store and reacts to state changes.
   */
  connectedCallback() {
    super.connectedCallback();
    this.subscribeToStores();
    this.subscribeToLocationStore();
  }

  /**
   * Derive the initial tab from URL state or defaultView prop
   *
   * @remarks
   * URL takes precedence over defaultView to support deep-linking.
   * "schedule" display maps to "schedule" tab; everything else is "browse".
   */
  private getInitialTab(): ActiveTab {
    const urlStateData = urlState.getState();
    if (urlStateData.display === "schedule") return "schedule";
    if (urlStateData.display === "map") return "browse";

    try {
      const saved = localStorage.getItem(TAB_STORAGE_KEY);
      if (saved === "schedule") return "schedule";
    } catch {
      // localStorage not available
    }

    return this.defaultView === "schedule" ? "schedule" : "browse";
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribeCart?.();
    this.unsubscribeUi?.();
    this.unsubscribeLocation?.();
  }

  /**
   * Save tab preference to localStorage
   */
  private saveTabPreference(tab: ActiveTab) {
    try {
      localStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {
      // localStorage not available
    }
  }

  /**
   * Subscribe to location store for skeleton removal
   *
   * @remarks
   * Removes skeleton components once location data is loaded.
   */
  private subscribeToLocationStore() {
    this.unsubscribeLocation = locationStore.subscribe(
      (state) => ({ tours: state.tours, markets: state.markets, isLoading: state.isLoading }),
      (selected) => {
        const hasData = selected.tours.length > 0 || selected.markets.length > 0;
        if (hasData && !selected.isLoading && !this.skeletonsRemoved) {
          this.removeSkeletons();
        }
      },
      { fireImmediately: true }
    );
  }

  /**
   * Remove skeleton components once real components are ready
   */
  private removeSkeletons() {
    this.skeletonsRemoved = true;

    document.querySelector("skeleton-locations-drawer")?.remove();
    document.querySelector("skeleton-cart-button")?.remove();

    // When starting in schedule tab the map never initializes,
    // so remove the static placeholder here so it doesn't cover the schedule.
    if (this.activeTab === "schedule") {
      document.getElementById("static-map-placeholder")?.remove();
    }

    const container = document.getElementById("page-map");
    container?.setAttribute("data-skeleton-hidden", "true");

    console.log("[LocalPickup] Skeletons removed");
  }

  /**
   * Update URL with current tab
   */
  private updateUrlWithTab(tab: ActiveTab) {
    const display: ViewType = tab === "schedule" ? "schedule" : "map";
    urlState.updateState({ display }, { debounce: false });
  }

  /**
   * Subscribe to stores for reactive state updates
   */
  private subscribeToStores() {
    this.unsubscribeCart = cartStore.subscribe(
      (state) => state.itemCount,
      (itemCount) => {
        this.cartItemCount = itemCount;
      },
      { fireImmediately: true }
    );

    this.unsubscribeUi = uiStore.subscribe(
      (state) => ({
        activeTab: state.activeTab,
        showMapOverlay: state.showMapOverlay,
        detailsSheet: state.detailsSheet,
        cartOpen: state.cartOpen,
      }),
      (selected, prevSelected) => {
        const tabChanged = selected.activeTab !== prevSelected?.activeTab;

        this.activeTab = selected.activeTab;
        this.showMapOverlay = selected.showMapOverlay;
        this.detailsSheetLocationId = selected.detailsSheet?.locationId ?? null;
        this.cartOpen = selected.cartOpen;

        // Ensure schedule is activated when schedule tab is selected
        // (handles both mobile tab bar and desktop view toggle)
        if (selected.activeTab === "schedule") {
          this.scheduleActivated = true;
          // Remove static map placeholder if it exists (covers schedule with z-index: 15)
          document.getElementById("static-map-placeholder")?.remove();
        }

        // Update URL when tab changes (for desktop view toggle)
        // Skip on initial load (prevSelected is undefined)
        if (tabChanged && prevSelected !== undefined) {
          this.updateUrlWithTab(selected.activeTab);
          this.saveTabPreference(selected.activeTab);
        }
      },
      {
        fireImmediately: true,
        equalityFn: (a, b) =>
          a.activeTab === b.activeTab &&
          a.showMapOverlay === b.showMapOverlay &&
          a.detailsSheet?.locationId === b.detailsSheet?.locationId &&
          a.cartOpen === b.cartOpen,
      }
    );
  }

  /**
   * Switch to a different tab
   */
  private handleTabChange(tab: ActiveTab) {
    if (tab === this.activeTab) return;

    if (tab === "schedule") {
      this.scheduleActivated = true;
      // Map overlay doesn't apply on schedule tab
      uiStore.getState().setShowMapOverlay(false);
    }

    uiStore.getState().setActiveTab(tab);
    this.saveTabPreference(tab);
    this.updateUrlWithTab(tab);

    this.dispatchEvent(
      new CustomEvent("tab-changed", {
        detail: { tab },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Toggle the map overlay in browse tab
   */
  private handleToggleMap() {
    uiStore.getState().toggleMapOverlay();
  }

  /**
   * Get user's current location and center map on it
   */
  private async handleUseCurrentLocation() {
    if (!GeolocationService.isSupported()) {
      console.error("Geolocation is not supported by this browser");
      return;
    }

    this.isGettingLocation = true;

    try {
      const location = await GeolocationService.getCurrentLocation();

      mapViewStore.getState().setMapCenter({
        lat: location.lat,
        lng: location.lng,
        zoom: 10,
      });

      locationStore.getState().setSearchPoint({
        lat: location.lat,
        lng: location.lng,
        radiusMiles: 50,
      });

      locationStore.getState().updateFilters({ searchQuery: "Current Location" });
    } catch (error) {
      console.error("Error getting current location:", error);
    } finally {
      this.isGettingLocation = false;
    }
  }

  /**
   * Handle location selected from list or schedule
   *
   * @remarks
   * Event detail varies by source:
   * - locations-list-item: { location: Location }
   * - schedule-stop: { locationId: number, stopId?: number }
   *
   * On mobile: opens a details bottom sheet.
   * On desktop: the existing sidebar panel handles this via locationStore selection.
   */
  private handleLocationSelected(e: CustomEvent) {
    e.stopPropagation();

    // Extract locationId from either event format
    const locationId = e.detail.locationId ?? e.detail.location?.id;
    const stopId = e.detail.stopId;

    if (!locationId) return;

    // Select in store (updates desktop sidebar)
    locationStore.getState().selectLocation(locationId);

    // Mobile: also open details bottom sheet
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;

    uiStore.getState().openDetailsSheet({ locationId, stopId });
  }

  /**
   * Handle close of the details bottom sheet
   */
  private handleDetailsSheetClose() {
    uiStore.getState().closeDetailsSheet();
    locationStore.getState().clearSelection();
  }

  /**
   * Toggle cart open/closed
   */
  private toggleCart() {
    uiStore.getState().toggleCart();
  }

  /**
   * Close the cart
   */
  private closeCart() {
    uiStore.getState().closeCart();
  }

  /**
   * Get the Location object for the details sheet
   */
  private get detailsSheetLocation() {
    if (this.detailsSheetLocationId === null) return null;
    return locationStore.getState().getAllLocations()
      .find((loc) => loc.id === this.detailsSheetLocationId) ?? null;
  }

  /**
   * Render the shared mobile header (logo + search)
   *
   * @remarks
   * Shows on both Browse and Schedule tabs on mobile.
   * Desktop sidebar has its own header, so this is hidden on md+.
   */
  private renderMobileHeader() {
    return html`
      <header class="md:hidden flex flex-col items-center px-4 pt-3 pb-4 bg-cream flex-shrink-0 border-b border-default relative z-30">
        <div class="flex items-center justify-between w-full mb-4">
          <a href="/" class="focus:outline-none focus:ring-2 focus:ring-primary rounded">
            <img
              src="${logoUrl}"
              alt="The Peach Truck"
              class="h-[40px] w-auto"
            />
          </a>
          <!-- Map toggle button (browse tab only) -->
          ${this.activeTab === "browse"
            ? html`
                <button
                  @click=${this.handleToggleMap}
                  class=${cn(
                    "flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors",
                    "shadow border",
                    this.showMapOverlay
                      ? "bg-brand text-white border-brand"
                      : "bg-surface text-muted border-default hover:text-default hover:bg-surface-muted"
                  )}
                  aria-label=${this.showMapOverlay
                    ? "View location list"
                    : "View map"}
                  aria-pressed=${this.showMapOverlay}
                >
                  <peach-icon
                    name=${this.showMapOverlay ? "menu" : "map"}
                    size="sm"
                  ></peach-icon>
                  ${this.showMapOverlay ? "List" : "Map"}
                </button>
              `
            : null}
        </div>

        <div class="w-full">
          <peach-map-filters
            ?is-getting-location=${this.isGettingLocation}
            @use-current-location=${this.handleUseCurrentLocation}
          ></peach-map-filters>
        </div>
      </header>
    `;
  }

  /**
   * Render the bottom tab bar (mobile only)
   */
  private renderTabBar() {
    const hasCartItems = this.cartItemCount > 0;

    return html`
      <nav
        class="fixed bottom-0 left-0 right-0 z-30 md:hidden bg-cream border-t border-default safe-area-bottom"
        aria-label="Main navigation"
      >
        <div class="flex items-stretch">
          <!-- Browse tab -->
          <button
            @click=${() => this.handleTabChange("browse")}
            class=${cn(
              "flex-1 flex flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors",
              this.activeTab === "browse"
                ? "text-brand"
                : "text-muted hover:text-default"
            )}
            aria-label="Browse locations"
            aria-pressed=${this.activeTab === "browse"}
          >
            <peach-icon
              name="map-pin"
              size="md"
              class=${this.activeTab === "browse" ? "text-brand" : ""}
            ></peach-icon>
            <span>Browse</span>
          </button>

          <!-- Schedule tab -->
          <button
            @click=${() => this.handleTabChange("schedule")}
            class=${cn(
              "flex-1 flex flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors",
              this.activeTab === "schedule"
                ? "text-brand"
                : "text-muted hover:text-default"
            )}
            aria-label="Schedule"
            aria-pressed=${this.activeTab === "schedule"}
          >
            <peach-icon
              name="calendar"
              size="md"
              class=${this.activeTab === "schedule" ? "text-brand" : ""}
            ></peach-icon>
            <span>Schedule</span>
          </button>

          <!-- Cart (not a tab, opens bottom sheet) -->
          <button
            @click=${this.toggleCart}
            class="flex-1 flex flex-col items-center gap-0.5 py-3 text-xs font-medium text-muted hover:text-default transition-colors relative"
            aria-label="Open cart${hasCartItems ? ` (${this.cartItemCount} items)` : ""}"
          >
            <span class="relative">
              <peach-icon name="shopping-cart" size="md"></peach-icon>
              ${hasCartItems
                ? html`
                    <span
                      class=${cn(
                        "absolute -top-1.5 -right-2",
                        "bg-brand-accent text-white text-xs font-bold",
                        "rounded-full min-w-[18px] h-[18px] px-0.5",
                        "flex items-center justify-center leading-none"
                      )}
                    >
                      ${this.cartItemCount}
                    </span>
                  `
                : null}
            </span>
            <span>Cart</span>
          </button>
        </div>
      </nav>
    `;
  }

  /**
   * Render floating cart button (top-right, desktop only)
   */
  private renderDesktopCartButton() {
    const hasCartItems = this.cartItemCount > 0;

    return html`
      <button
        @click=${this.toggleCart}
        class=${cn(
          "fixed top-4 right-4 z-40",
          "hidden md:flex items-center justify-center",
          "bg-surface shadow-lg rounded-full p-3",
          "hover:bg-surface-muted transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
        )}
        aria-label="Open cart"
      >
        <peach-icon name="shopping-cart" size="md" class="text-default"></peach-icon>
        ${hasCartItems
          ? html`
              <span
                class=${cn(
                  "absolute -top-1 -right-1",
                  "bg-brand-accent text-white text-xs font-bold",
                  "rounded-full min-w-[20px] h-5 px-1",
                  "flex items-center justify-center"
                )}
              >
                ${this.cartItemCount}
              </span>
            `
          : null}
      </button>
    `;
  }

  /**
   * Render the map overlay that appears on top of the browse list (mobile only)
   *
   * @remarks
   * This is a full-screen map overlay toggled by the Map button in browse tab.
   * It renders on top of the locations list so the list is preserved underneath.
   * iOS Safari ignores pointer-events:none on fixed elements, blocking scroll
   * underneath, so we only render when actually shown.
   */
  private renderMapOverlay() {
    if (!this.showMapOverlay) return null;

    return html`
      <div
        class="fixed inset-0 z-10 md:hidden bottom-[57px]"
      >
        <peach-map-canvas class="absolute inset-0 h-full w-full"></peach-map-canvas>
      </div>
    `;
  }

  /**
   * Render the browse tab content (locations list)
   */
  private renderBrowseTab() {
    const isActive = this.activeTab === "browse";

    return html`
      <div
        class=${cn(
          "absolute inset-0",
          // Leave room for tab bar on mobile
          "bottom-[57px] md:bottom-0",
          // Active state - display mode applied only when visible
          isActive
            ? "block md:relative md:h-full md:flex md:flex-row"
            : "hidden"
        )}
      >
        <!-- Locations panel (sidebar on desktop, full panel on mobile) -->
        <peach-locations-drawer
          @location-selected=${this.handleLocationSelected}
        ></peach-locations-drawer>

        <!-- Desktop: map fills the right panel -->
        <main class="flex-1 relative h-full overflow-hidden hidden md:block">
          <peach-map-canvas class="absolute inset-0 h-full w-full"></peach-map-canvas>
        </main>
      </div>
    `;
  }

  /**
   * Render the schedule tab content
   */
  private renderScheduleTab() {
    const isActive = this.activeTab === "schedule";

    return html`
      <div
        class=${cn(
          "absolute inset-0",
          // Leave room for tab bar on mobile
          "bottom-[57px] md:bottom-0",
          // Active state - display mode applied only when visible
          isActive
            ? "block md:relative md:h-full md:flex md:flex-row"
            : "hidden"
        )}
      >
        <!-- Desktop: sidebar persists in schedule tab (flex-shrink-0 to not squish) -->
        <div class="hidden md:flex md:flex-shrink-0 md:h-full border-r border-default">
          <peach-locations-drawer
            @location-selected=${this.handleLocationSelected}
          ></peach-locations-drawer>
        </div>

        <!-- Schedule fills the main panel -->
        <main class="flex-1 relative h-full overflow-hidden">
          ${this.scheduleActivated
            ? html`<peach-schedule-view
                class="absolute inset-0 h-full w-full overflow-hidden flex flex-col"
              ></peach-schedule-view>`
            : null}
        </main>
      </div>
    `;
  }

  /**
   * Render the location details bottom sheet (mobile only)
   *
   * @remarks
   * Desktop uses the existing sidebar panel in locations-drawer.
   * Only renders when a location is selected - light DOM can't hide children.
   * Content passed via .content property since slots don't work in light DOM.
   */
  private renderDetailsSheet() {
    const location = this.detailsSheetLocation;
    if (!location) return null;

    const sheetContent = html`
      <div class="flex flex-col flex-1 overflow-hidden min-h-0">
        <peach-location-details
          class="flex flex-col flex-1 min-h-0 overflow-hidden"
          .location=${location}
        ></peach-location-details>
      </div>
    `;

    return html`
      <peach-bottom-sheet
        class="md:hidden"
        open
        height="full"
        aria-label="Details for ${location.name}"
        .content=${sheetContent}
        @close=${this.handleDetailsSheetClose}
      ></peach-bottom-sheet>
    `;
  }

  /**
   * Render the cart bottom sheet (mobile only)
   *
   * @remarks
   * Desktop uses peach-cart-drawer in its existing slide-in-from-right form.
   * Only renders when cart is open - light DOM can't hide children.
   * Content passed via .content property since slots don't work in light DOM.
   */
  private renderCartSheet() {
    if (!this.cartOpen) return null;

    const sheetContent = html`
      <div class="flex flex-col flex-1 overflow-hidden min-h-0">
        <peach-cart-drawer content-only class="flex-1 min-h-0 overflow-hidden flex flex-col"></peach-cart-drawer>
      </div>
    `;

    return html`
      <peach-bottom-sheet
        class="md:hidden"
        open
        height="full"
        aria-label="Shopping cart"
        .content=${sheetContent}
        @close=${this.closeCart}
      ></peach-bottom-sheet>
    `;
  }

  render() {
    return html`
      <div
        class="relative h-full w-full flex flex-col"
        @location-selected=${this.handleLocationSelected}
      >
        <!-- Mobile header (shared by all tabs) -->
        ${this.renderMobileHeader()}

        <!-- Tab content panels -->
        <div class="flex-1 relative overflow-hidden min-h-0">
          ${this.renderBrowseTab()}
          ${this.renderScheduleTab()}
        </div>

        <!-- Map overlay (mobile browse tab only) -->
        ${this.renderMapOverlay()}

        <!-- Mobile tab bar -->
        ${this.renderTabBar()}

        <!-- Desktop floating cart button -->
        ${this.renderDesktopCartButton()}

        <!-- Desktop cart drawer (existing slide-from-right behavior) -->
        <div class="hidden md:contents">
          <peach-cart-drawer></peach-cart-drawer>
        </div>

        <!-- Mobile bottom sheets -->
        ${this.renderDetailsSheet()}
        ${this.renderCartSheet()}
      </div>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-local-pickup-view": LocalPickupViewComponent;
  }
}
